import React, { useState, useEffect, useCallback } from "react";
import { transferSummaryService } from "../service/Transfersummaryservice";

const ALL_LOCATIONS = ["Khejuri", "Chaitanyapur", "Nandigram", "Ms Enterprise", "Other"];

const LC = {
  Khejuri:         { bg: "#eff6ff", border: "#3b82f6", badge: "#2563eb", text: "#1e40af", light: "#dbeafe" },
  Chaitanyapur:    { bg: "#f0fdf4", border: "#22c55e", badge: "#16a34a", text: "#14532d", light: "#bbf7d0" },
  Nandigram:       { bg: "#fff7ed", border: "#f97316", badge: "#ea580c", text: "#7c2d12", light: "#fed7aa" },
  "Ms Enterprise": { bg: "#fdf4ff", border: "#a855f7", badge: "#9333ea", text: "#581c87", light: "#e9d5ff" },
  "Other":         { bg: "#f8fafc", border: "#94a3b8", badge: "#64748b", text: "#1e293b", light: "#e2e8f0" },
};

const fmt  = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const fmtN = (n) => Number(n || 0).toLocaleString("en-IN");

// ── Icons ─────────────────────────────────────────────────────────────────────
const IcoPin = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
  </svg>
);
const IcoArrowRight = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <path d="M4 11h12.17l-5.59-5.59L12 4l8 8-8 8-1.41-1.41L16.17 13H4z"/>
  </svg>
);
const IcoArrowLeft = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ transform: "rotate(180deg)" }}>
    <path d="M4 11h12.17l-5.59-5.59L12 4l8 8-8 8-1.41-1.41L16.17 13H4z"/>
  </svg>
);
const IcoBike = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM5 12c-2.8 0-5 2.2-5 5s2.2 5 5 5 5-2.2 5-5-2.2-5-5-5zm0 8.5c-1.9 0-3.5-1.6-3.5-3.5S3.1 13.5 5 13.5 8.5 15.1 8.5 17 6.9 20.5 5 20.5zm5.8-10l2.4-2.4.8.8c1.3 1.3 3 2.1 5.1 2.1V11c-1.5 0-2.7-.6-3.6-1.5l-1.9-1.9c-.5-.4-1-.6-1.6-.6s-1.1.2-1.4.6L7.8 10c-.4.4-.6.9-.6 1.4 0 .6.2 1.1.6 1.4L11 15.1V19h2v-5l-2.2-2.2 2-2.3zM19 12c-2.8 0-5 2.2-5 5s2.2 5 5 5 5-2.2 5-5-2.2-5-5-5zm0 8.5c-1.9 0-3.5-1.6-3.5-3.5s1.6-3.5 3.5-3.5 3.5 1.6 3.5 3.5-1.6 3.5-3.5 3.5z"/>
  </svg>
);
const IcoChevron = ({ up }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"
    style={{ transform: up ? "rotate(180deg)" : "none", transition: "transform .2s" }}>
    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
  </svg>
);

// ── Location badge ─────────────────────────────────────────────────────────────
// ✅ Uses baseKey for LC lookup so "Other (X)" gets Other's colors
const LocBadge = ({ name }) => {
  const baseKey = name && name.startsWith("Other") ? "Other" : name;
  const c = LC[baseKey] || {};
  return (
    <span style={{
      fontSize: 10, fontWeight: 700,
      color: c.text || "#374151",
      background: c.bg || "#f3f4f6",
      border: `1px solid ${c.border || "#e5e7eb"}`,
      borderRadius: 5, padding: "2px 8px", whiteSpace: "nowrap",
    }}>{name}</span>
  );
};

// ── Stat box ──────────────────────────────────────────────────────────────────
const StatBox = ({ label, count, price, bg, border, textColor, accentColor }) => (
  <div style={{
    flex: "1 1 150px", background: bg, border: `1.5px solid ${border}`,
    borderRadius: 10, padding: "12px 14px",
  }}>
    <div style={{ fontSize: 9, fontWeight: 800, color: textColor, letterSpacing: 1, textTransform: "uppercase", marginBottom: 5 }}>
      {label}
    </div>
    <div style={{ fontSize: 22, fontWeight: 900, color: textColor, lineHeight: 1 }}>
      {fmtN(count)}
      <span style={{ fontSize: 11, fontWeight: 600 }}> bikes</span>
    </div>
    <div style={{ fontSize: 13, fontWeight: 700, color: accentColor || textColor, marginTop: 3 }}>{fmt(price)}</div>
  </div>
);

// ── Transfer row ──────────────────────────────────────────────────────────────
const TransferRow = ({ direction, thisLoc, otherLoc, count, price }) => {
  const otherBaseKey = otherLoc && otherLoc.startsWith("Other") ? "Other" : otherLoc;
  const oc = LC[otherBaseKey] || {};
  const isOut = direction === "out";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
      padding: "10px 14px",
      background: isOut ? "#fff9f9" : "#f6fff9",
      border: `1px solid ${isOut ? "#fecaca" : "#bbf7d0"}`,
      borderRadius: 8, marginBottom: 6,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flex: "0 0 auto" }}>
        {isOut ? (
          <>
            <LocBadge name={thisLoc} />
            <span style={{ color: "#dc2626" }}><IcoArrowRight /></span>
            <LocBadge name={otherLoc} />
          </>
        ) : (
          <>
            <LocBadge name={otherLoc} />
            <span style={{ color: "#16a34a" }}><IcoArrowRight /></span>
            <LocBadge name={thisLoc} />
          </>
        )}
      </div>

      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 5 }}>
        <span style={{ color: oc.badge || "#6b7280" }}><IcoBike /></span>
        <span style={{ fontWeight: 900, fontSize: 15, color: "#111827" }}>{fmtN(count)}</span>
        <span style={{ fontSize: 11, color: "#6b7280" }}>bikes</span>
      </div>

      <div style={{
        padding: "4px 10px",
        background: isOut ? "#fef2f2" : "#f0fdf4",
        border: `1px solid ${isOut ? "#fca5a5" : "#86efac"}`,
        borderRadius: 7, textAlign: "right",
      }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: isOut ? "#dc2626" : "#16a34a", letterSpacing: .5 }}>
          PURCHASE AMT
        </div>
        <div style={{ fontSize: 13, fontWeight: 800, color: isOut ? "#991b1b" : "#166534" }}>
          {fmt(price)}
        </div>
      </div>
    </div>
  );
};

// ── Bike detail table ─────────────────────────────────────────────────────────
function BikeTable({ bikes }) {
  if (!bikes || bikes.length === 0)
    return <p style={{ padding: "10px 0", color: "#9ca3af", fontSize: 12 }}>No bikes found.</p>;
  return (
    <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #e5e7eb", marginTop: 8 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
        <thead>
          <tr style={{ background: "#f9fafb" }}>
            {["#", "Challan", "Model", "Colour", "Chasis", "Engine", "Purchase ₹", "First Loc", "Current Loc", "Status"].map(h => (
              <th key={h} style={{
                padding: "8px 10px", textAlign: "left", fontWeight: 700,
                fontSize: 10, color: "#374151", borderBottom: "1.5px solid #e5e7eb", whiteSpace: "nowrap",
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bikes.map((b, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #f3f4f6", background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
              <td style={{ padding: "8px 10px", color: "#9ca3af" }}>{i + 1}</td>
              <td style={{ padding: "8px 10px", fontWeight: 600, color: "#2563eb" }}>{b.challanNo || "—"}</td>
              <td style={{ padding: "8px 10px" }}>
                <div style={{ fontWeight: 700 }}>{b.model || "—"}</div>
                <div style={{ fontSize: 9, color: "#9ca3af" }}>{b.skuCode}</div>
              </td>
              <td style={{ padding: "8px 10px" }}>{b.colour || "—"}</td>
              <td style={{ padding: "8px 10px", fontFamily: "monospace", fontSize: 10 }}>{b.chasis || "—"}</td>
              <td style={{ padding: "8px 10px", fontFamily: "monospace", fontSize: 10 }}>{b.engine || "—"}</td>
              <td style={{ padding: "8px 10px", fontWeight: 700 }}>{fmt(b.purchasePrice)}</td>
              {/* ✅ LocBadge handles "Other (X)" via baseKey internally */}
              <td style={{ padding: "8px 10px" }}><LocBadge name={b.firstLocation} /></td>
              <td style={{ padding: "8px 10px" }}><LocBadge name={b.currentLocation} /></td>
              <td style={{ padding: "8px 10px" }}>
                <span style={{
                  background: b.status === "IN_STOCK" ? "#f0fdf4" : b.status === "SOLD" ? "#fef2f2" : "#fffbeb",
                  color:      b.status === "IN_STOCK" ? "#16a34a" : b.status === "SOLD" ? "#dc2626" : "#d97706",
                  borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 700,
                }}>{b.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Filter bar ────────────────────────────────────────────────────────────────
// ✅ Added engine / chasis search inputs alongside existing period filters
function FilterBar({ filters, onChange, onApply, onReset, show }) {
  if (!show) return null;
  const inp = {
    padding: "7px 11px", border: "1.5px solid #e5e7eb", borderRadius: 8,
    fontSize: 13, color: "#111827", background: "#fafafa", outline: "none", minWidth: 130,
  };
  return (
    <div style={{
      background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12,
      padding: "18px 20px", marginBottom: 20, boxShadow: "0 1px 4px rgba(0,0,0,.05)",
    }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
        {["allTime", "year", "month", "range"].map(t => (
          <button key={t} onClick={() => onChange("periodType", t)} style={{
            padding: "6px 14px", borderRadius: 20, border: "1.5px solid",
            borderColor: filters.periodType === t ? "#2563eb" : "#e5e7eb",
            background:  filters.periodType === t ? "#eff6ff" : "#fff",
            color:       filters.periodType === t ? "#2563eb" : "#6b7280",
            fontWeight:  filters.periodType === t ? 700 : 400,
            cursor: "pointer", fontSize: 12,
          }}>{{ allTime: "All Time", year: "Year", month: "Month", range: "Date Range" }[t]}</button>
        ))}
        {(filters.periodType === "year" || filters.periodType === "month") && (
          <input type="number" placeholder="Year e.g. 2024" value={filters.year}
            onChange={e => onChange("year", e.target.value)} style={inp} />
        )}
        {filters.periodType === "month" && (
          <select value={filters.month} onChange={e => onChange("month", e.target.value)} style={inp}>
            <option value="">Month</option>
            {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
              .map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        )}
        {filters.periodType === "range" && (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <label style={{ fontSize: 10, color: "#9ca3af" }}>From</label>
              <input type="date" value={filters.startDate} onChange={e => onChange("startDate", e.target.value)} style={inp} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <label style={{ fontSize: 10, color: "#9ca3af" }}>To</label>
              <input type="date" value={filters.endDate} onChange={e => onChange("endDate", e.target.value)} style={inp} />
            </div>
          </>
        )}
      </div>

      {/* ✅ Engine / Chasis search row — independent of periodType, always available */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <label style={{ fontSize: 10, color: "#9ca3af" }}>Engine No.</label>
          <input
            type="text"
            placeholder="Search engine no."
            value={filters.engine}
            onChange={e => onChange("engine", e.target.value)}
            style={{ ...inp, fontFamily: "monospace" }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <label style={{ fontSize: 10, color: "#9ca3af" }}>Chasis No.</label>
          <input
            type="text"
            placeholder="Search chasis no."
            value={filters.chasis}
            onChange={e => onChange("chasis", e.target.value)}
            style={{ ...inp, fontFamily: "monospace" }}
          />
        </div>
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={onApply} style={{
          background: "#2563eb", color: "#fff", border: "none", borderRadius: 8,
          padding: "8px 22px", fontWeight: 700, fontSize: 12, cursor: "pointer",
        }}>APPLY</button>
        <button onClick={onReset} style={{
          background: "#fff", color: "#374151", border: "1.5px solid #d1d5db",
          borderRadius: 8, padding: "8px 18px", fontWeight: 600, fontSize: 12, cursor: "pointer",
        }}>RESET</button>
      </div>
    </div>
  );
}

// ── Section divider label ─────────────────────────────────────────────────────
const SectionLabel = ({ children, icon }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: 6,
    fontSize: 10, fontWeight: 800, color: "#64748b",
    letterSpacing: 1, textTransform: "uppercase", marginBottom: 10,
  }}>
    {icon && <span>{icon}</span>}
    {children}
  </div>
);

// ── Location card ─────────────────────────────────────────────────────────────
function LocationCard({ locName, data, bikeCache, onToggleBikes }) {
  // ✅ "Other (X)" uses "Other" colors
  const baseKey = locName.startsWith("Other") ? "Other" : locName;
  const c = LC[baseKey] || {};
  if (!data) return null;

  const { entered, transferredOut, transferredIn, net } = data;
  const outBreakdown = Object.entries(transferredOut?.breakdown || {});
  const inBreakdown  = Object.entries(transferredIn?.breakdown  || {});
  const cache        = bikeCache[locName] || {};

  return (
    <div style={{
      background: "#fff", borderRadius: 16,
      border: `2px solid ${c.border}`, marginBottom: 24,
      overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,.07)",
    }}>

      {/* ── Card header ── */}
      <div style={{
        background: `linear-gradient(135deg, ${c.bg} 0%, #fff 100%)`,
        padding: "16px 22px", borderBottom: `2px solid ${c.border}`,
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      }}>
        <span style={{ color: c.badge }}><IcoPin /></span>
        {/* ✅ Shows full "Other (X)" name in header */}
        <span style={{ fontWeight: 900, fontSize: 18, color: c.text }}>{locName}</span>

        <span style={{
          background: c.badge, color: "#fff",
          borderRadius: 20, padding: "3px 12px", fontSize: 12, fontWeight: 700,
        }}>
          {fmtN(entered.count)} entered
        </span>

        <span style={{
          background: "#fff", border: `1.5px solid ${c.border}`,
          borderRadius: 20, padding: "3px 12px", fontSize: 12, fontWeight: 700, color: c.text,
        }}>
          Net: {fmtN(net.count)} · {fmt(net.totalPurchasePrice)}
        </span>

        <span style={{ marginLeft: "auto", fontWeight: 800, fontSize: 14, color: c.text }}>
          {fmt(entered.totalPurchasePrice)}
        </span>
      </div>

      {/* ── Card body ── */}
      <div style={{ padding: "20px 22px" }}>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
          <StatBox
            label="Entered Here (Purchase Booked)"
            count={entered.count} price={entered.totalPurchasePrice}
            bg="#f0fdf4" border="#86efac" textColor="#166534" accentColor="#16a34a"
          />
          <StatBox
            label="Transferred Out Total"
            count={transferredOut.total.count} price={transferredOut.total.totalPurchasePrice}
            bg="#fef2f2" border="#fca5a5" textColor="#991b1b" accentColor="#dc2626"
          />
          <StatBox
            label="Transferred In Total"
            count={transferredIn.total.count} price={transferredIn.total.totalPurchasePrice}
            bg="#eff6ff" border="#93c5fd" textColor="#1e40af" accentColor="#2563eb"
          />
          <StatBox
            label="Net (Entered + In − Out)"
            count={net.count} price={net.totalPurchasePrice}
            bg={c.bg} border={c.light || c.border} textColor={c.text} accentColor={c.badge}
          />
        </div>

        {/* ── Transferred OUT ── */}
        {outBreakdown.length > 0 && (
          <div style={{ marginBottom: 22 }}>
            <SectionLabel icon="↗">Transferred OUT from {locName} — per destination</SectionLabel>
            {outBreakdown.map(([dest, d]) => (
              <TransferRow key={dest} direction="out" thisLoc={locName} otherLoc={dest}
                count={d.count} price={d.totalPurchasePrice} />
            ))}
          </div>
        )}

        {/* ── Transferred IN ── */}
        {inBreakdown.length > 0 && (
          <div style={{ marginBottom: 22 }}>
            <SectionLabel icon="↙">Transferred IN to {locName} — per source</SectionLabel>
            {inBreakdown.map(([src, d]) => (
              <TransferRow key={src} direction="in" thisLoc={locName} otherLoc={src}
                count={d.count} price={d.totalPurchasePrice} />
            ))}
          </div>
        )}

        {/* ── Bike list toggle ── */}
        <div>
          <button
            onClick={() => onToggleBikes(locName)}
            style={{
              display: "flex", alignItems: "center", gap: 8, width: "100%",
              background: cache.open ? c.bg : "#f8fafc",
              border: `1.5px solid ${cache.open ? c.border : "#e2e8f0"}`,
              borderRadius: 9, padding: "10px 16px", cursor: "pointer",
              color: cache.open ? c.text : "#475569", fontWeight: 700, fontSize: 12,
            }}
          >
            <IcoBike />
            {cache.open ? "Hide" : "Show"} Bikes Currently at {locName}
            {cache.data && (
              <span style={{
                background: c.badge, color: "#fff",
                borderRadius: 20, padding: "1px 9px", fontSize: 11, marginLeft: 4,
              }}>{cache.data.count}</span>
            )}
            <span style={{ marginLeft: "auto" }}><IcoChevron up={!!cache.open} /></span>
          </button>

          {cache.open && (
            <div style={{ marginTop: 10 }}>
              {cache.loading
                ? <p style={{ color: "#94a3b8", fontSize: 13, padding: "10px 0" }}>Loading...</p>
                : <BikeTable bikes={cache.data?.bikes} />
              }
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function TransferSummaryPage() {
  const [showFilters, setShowFilters] = useState(true);
  // ✅ Added engine, chasis to filters state
  const [filters, setFilters] = useState({
    periodType: "allTime", year: "", month: "", startDate: "", endDate: "", engine: "", chasis: "",
  });

  const [summary,   setSummary]   = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [bikeCache, setBikeCache] = useState({});

  // ✅ engine / chasis (trimmed) now included in every request, regardless of periodType
  const buildParams = useCallback(() => {
    const p = {};
    if (filters.periodType === "year"  && filters.year)                         p.year = filters.year;
    if (filters.periodType === "month" && filters.year)                         { p.year = filters.year; p.month = filters.month; }
    if (filters.periodType === "range" && filters.startDate && filters.endDate) { p.startDate = filters.startDate; p.endDate = filters.endDate; }
    if (filters.engine && filters.engine.trim())                                p.engine = filters.engine.trim();
    if (filters.chasis && filters.chasis.trim())                                p.chasis = filters.chasis.trim();
    return p;
  }, [filters]);

  // ✅ Fixed: fetchSummary calls getSummary (not getDetail)
  const fetchSummary = useCallback(async () => {
    setLoading(true); setError(null); setBikeCache({});
    try {
      const data = await transferSummaryService.getSummary(buildParams());
      setSummary(data);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load summary");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { fetchSummary(); }, []);

  const handleApply = () => fetchSummary();
  const handleReset = () => {
    setFilters({ periodType: "allTime", year: "", month: "", startDate: "", endDate: "", engine: "", chasis: "" });
    setTimeout(fetchSummary, 0);
  };

  const handleToggleBikes = useCallback(async (locName) => {
    const cur = bikeCache[locName] || {};

    if (!cur.data && !cur.loading) {
      setBikeCache(c => ({ ...c, [locName]: { loading: true, data: null, open: true } }));
      try {
        // ✅ Send raw "Other" to backend, not "Other (X)"
        const rawLocation = locName.startsWith("Other (") ? "Other" : locName;
        const data = await transferSummaryService.getDetail({ location: rawLocation, ...buildParams() });
        setBikeCache(c => ({ ...c, [locName]: { loading: false, data, open: true } }));
      } catch {
        setBikeCache(c => ({ ...c, [locName]: { loading: false, data: { bikes: [], count: 0 }, open: true } }));
      }
      return;
    }

    setBikeCache(c => ({ ...c, [locName]: { ...c[locName], open: !c[locName]?.open } }));
  }, [bikeCache, buildParams]);

  // ✅ Grand totals from dynamic keys
  const grand = summary
    ? Object.keys(summary.locationSummary || {}).reduce((a, loc) => {
        a.count += summary.locationSummary?.[loc]?.entered?.count || 0;
        a.price += summary.locationSummary?.[loc]?.entered?.totalPurchasePrice || 0;
        return a;
      }, { count: 0, price: 0 })
    : null;

  return (
    <div style={{ minHeight: "100vh", background: "#f1f5f9", fontFamily: "'Segoe UI', sans-serif" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 16px" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
            <div style={{
              width: 44, height: 44, background: "#2563eb", borderRadius: 12,
              display: "flex", alignItems: "center", justifyContent: "center", color: "#fff",
            }}><IcoBike /></div>
            <div>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: "#0f172a" }}>
                Transfer Summary
              </h1>
              <p style={{ margin: 0, fontSize: 12, color: "#64748b" }}>
                {summary
                  ? `${summary.totalBikesInPeriod} bikes · ${summary.period} · Location-wise flow`
                  : "Per-location entry, transfer in & out"}
              </p>
            </div>
          </div>
          <button onClick={() => setShowFilters(f => !f)} style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "#2563eb", color: "#fff", border: "none",
            borderRadius: 9, padding: "8px 16px", fontWeight: 700, fontSize: 12, cursor: "pointer",
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
              <path d="M4.25 5.61C6.27 8.2 10 13 10 13v6c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-6s3.72-4.8 5.74-7.39A.998.998 0 0 0 18.95 4H5.04a1 1 0 0 0-.79 1.61z"/>
            </svg>
            {showFilters ? "HIDE" : "SHOW"} FILTERS
          </button>
        </div>

        <FilterBar
          filters={filters}
          onChange={(k, v) => setFilters(f => ({ ...f, [k]: v }))}
          onApply={handleApply}
          onReset={handleReset}
          show={showFilters}
        />

        {/* ✅ Top strip — dynamic keys, baseKey for colors, full locName for display */}
        {!loading && summary && (
          <div style={{ display: "flex", gap: 10, marginBottom: 22, flexWrap: "wrap" }}>
            {Object.keys(summary.locationSummary || {}).map(loc => {
              const baseKey = loc.startsWith("Other") ? "Other" : loc;
              const c = LC[baseKey] || {};
              const d = summary.locationSummary?.[loc];
              return (
                <div key={loc} style={{
                  flex: "1 1 160px",
                  background: `linear-gradient(135deg, ${c.bg} 0%, #fff 100%)`,
                  border: `2px solid ${c.border}`,
                  borderRadius: 12, padding: "12px 16px",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 5 }}>
                    <span style={{ color: c.badge }}><IcoPin /></span>
                    {/* ✅ Shows "Other (X)" in the strip */}
                    <span style={{ fontWeight: 800, fontSize: 13, color: c.text }}>{loc}</span>
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: c.text }}>
                    {fmtN(d?.entered?.count || 0)}
                    <span style={{ fontSize: 10, fontWeight: 600 }}> entered</span>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: c.badge }}>
                    {fmt(d?.entered?.totalPurchasePrice)}
                  </div>
                  <div style={{ fontSize: 11, color: "#475569", marginTop: 3, borderTop: `1px solid ${c.border}`, paddingTop: 3 }}>
                    Net: <strong style={{ color: c.text }}>{fmtN(d?.net?.count || 0)} · {fmt(d?.net?.totalPurchasePrice)}</strong>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {loading && <div style={{ textAlign: "center", padding: 60, color: "#94a3b8", fontSize: 14 }}>Loading...</div>}
        {error && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "14px 18px", color: "#dc2626", fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* ✅ Location cards — dynamic keys */}
        {!loading && !error && summary && Object.keys(summary.locationSummary || {}).map(locName => (
          <LocationCard
            key={locName}
            locName={locName}
            data={summary.locationSummary?.[locName]}
            bikeCache={bikeCache}
            onToggleBikes={handleToggleBikes}
          />
        ))}

      </div>
    </div>
  );
}